import { useLoginStore } from '@/login/stores/useLoginStore';
import type { Collection, Model } from '@/newApi/services/ResgateManager';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import type { CountryCode, EmploymentSalaryType } from '@sysarb/sysarb-types/general/enums';
import type { EmploymentCountryLegalEntityStatistics, EmploymentStatistics } from '@sysarb/sysarb-types-v2/services/v1/base-registry';
import type { QueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';

export type EmploymentStatisticsFilters = {
    countryCode: CountryCode;
    /** Optional. When omitted, the backend returns country-wide aggregates. */
    legalEntityId?: string | null;
    salaryType: EmploymentSalaryType;
};

type EmploymentStatisticsOptions = {
    queries: {
        getEmploymentStatisticsPerCountryAndLegalEntity: () => QueryOptions<Collection<EmploymentCountryLegalEntityStatistics> | null, Error, EmploymentCountryLegalEntityStatistics[]>;
        getEmploymentStatistics: (filters: EmploymentStatisticsFilters | null) => QueryOptions<Model<EmploymentStatistics> | null, Error>;
    };
};

function getVersion(): 'v1' | 'v2' {
    return 'v2';
}

export const employmentStatistics: EmploymentStatisticsOptions = {
    queries: {
        getEmploymentStatisticsPerCountryAndLegalEntity: (): QueryOptions<Collection<EmploymentCountryLegalEntityStatistics> | null, Error, EmploymentCountryLegalEntityStatistics[]> => {
            const { instanceTopic, currentOrganisationNodeId } = useLoginStore();
            const topic = `v2.${instanceTopic}.${currentOrganisationNodeId}.base-register.section-manager.country-and-entity.employment-statistics`;

            return queryOptions<Collection<EmploymentCountryLegalEntityStatistics> | null, Error, EmploymentCountryLegalEntityStatistics[]>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['section-manager', 'country-and-entity-employment-statistics', topic],
                queryFn: async (): Promise<Collection<EmploymentCountryLegalEntityStatistics> | null> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.collection<EmploymentCountryLegalEntityStatistics>(topic);
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
                select: data => data?.list ?? [],
                enabled: () => currentOrganisationNodeId != null,
            });
        },
        getEmploymentStatistics: (filters: EmploymentStatisticsFilters | null): QueryOptions<Model<EmploymentStatistics> | null, Error> => {
            const { instanceTopic } = useLoginStore();
            const version = getVersion();

            // Build query string only when filters supplied; skip null/empty values.
            let queryString = '';
            if (filters != null) {
                const searchParams = new URLSearchParams();
                searchParams.set('countryCode', filters.countryCode);
                if (filters.legalEntityId != null && filters.legalEntityId !== '') {
                    searchParams.set('legalEntityId', filters.legalEntityId);
                }
                searchParams.set('salaryType', String(filters.salaryType));
                queryString = `?${searchParams.toString()}`;
            }

            const topic = `${version}.${instanceTopic}.base-register.employments-statistics${queryString}`;

            return queryOptions<Model<EmploymentStatistics> | null, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['section-manager', 'employment-statistics', topic],
                queryFn: async (): Promise<Model<EmploymentStatistics> | null> => {
                    const res = await ResgateManager.model<EmploymentStatistics>(topic);
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
                enabled: () => filters != null,
            });
        },
    },
};
